import _ from 'lodash'
import { safeAwait } from '@vzmi/component_ec_nodejs_util/dist/lib/FetchDataUtil'
import { throwIfError } from '../../../../../lib/reserviceErrorHelpers'
import {
  getCustomError,
  getDataFromHtml,
  getSafeCopy,
  getExternalLabel,
  getFirstUrlFromSrcset,
  isRegulatoryCertificationAttributeLabel,
  appendProductImageHash
} from '../helpers'
import useCorsCheck from './useCorsCheck'
import useCloneRequestController from './useCloneRequestController'
import useExternalHtml from './useExternalHtml'
import useBrands from './useBrands'
import useCategoryAttributes from './useCategoryAttributes'
import { CAUSE_CODE, SCHEMA } from '../constants'

/**
 * Returns the Shopee listing schema for getDataFromHtml.
 * @param {Object} requestController - From useCloneRequestController
 * @param {Function} getBrandIdByName - From useBrands, resolves brand name to id
 * @param {Function} [match] - From useCategoryAttributes, (attr) => boolean
 * @returns {Object} Schema for getDataFromHtml
 */
export const getShopeeSchema = (
  _requestController,
  { getBrandIdByName, match }
) => ({
  [SCHEMA.TITLE]: {
    type: 'custom',
    selector: 'meta[property="og:title"]',
    attribute: 'content',
    callback: (html = '') => _.trim(html.split('|')[0])
  },
  [SCHEMA.BRAND]: {
    type: 'custom',
    selector: '.product-detail',
    attribute: 'innerHTML',
    callback: (html = '') => {
      if (typeof html !== 'string' || _.isEmpty(html.trim())) {
        return { name: '', id: getBrandIdByName('') }
      }

      const doc = new DOMParser().parseFromString(html, 'text/html')
      const attributesElements = doc.querySelectorAll('section>div>div')
      const brand = _.reduce(Array.from(attributesElements), (result, element) => {
        const attributeTitle = _.get(element.querySelector('h3'), 'innerText')
        if (_.trim(attributeTitle) === '品牌') {
          return _.trim(_.get(element.querySelector('h3+*'), 'innerText', ''))
        }
        return result
      }, '')
      return { name: (brand || '').trim(), id: getBrandIdByName(brand) }
    }
  },
  [SCHEMA.ATTRIBUTES_TABLE]: {
    type: 'custom',
    selector: '.product-detail',
    attribute: 'innerHTML',
    callback: (html = '') => {
      if (typeof html !== 'string' || _.isEmpty(html.trim())) {
        return []
      }

      const doc = new DOMParser().parseFromString(html, 'text/html')
      const nodes = Array.from(doc.querySelectorAll('section>div>div'))
      const rows = nodes.map((el) => {
        const label = _.trim(_.get(el.querySelector('h3'), 'innerText', ''))
        const value = _.trim(_.get(el.querySelector('h3+*'), 'innerText', ''))
        return { label, value }
      })
      return rows
        .filter((row) => {
          return (
            row.label !== '分類' &&
            (!_.isEmpty(row.label) && !_.isEmpty(row.value))
          )
        })
        .map((row) => ({
          ...row,
          label:
            match(row) && !isRegulatoryCertificationAttributeLabel(row.label)
              ? row.label
              : getExternalLabel(row.label)
        }))
    }
  },
  [SCHEMA.PRODUCT_COPY]: {
    type: 'custom',
    selector: '.product-detail section:nth-child(2) div',
    attribute: 'innerHTML',
    callback: async (goodsHtml = '') =>
      _.isEmpty(goodsHtml) ? '' : getSafeCopy(goodsHtml)
  },
  [SCHEMA.PRODUCT_IMAGES]: {
    type: 'custom',
    selector: '.page-product__breadcrumb ~ section',
    attribute: 'innerHTML',
    callback: (html = '') => {
      if (typeof html !== 'string' || _.isEmpty(html.trim())) {
        return []
      }

      const doc = new DOMParser().parseFromString(html, 'text/html')
      const masks = Array.from(doc.querySelectorAll('.thumbnail-selected-mask'))
      const urls = masks
        .map((mask) => {
          const image = mask.parentNode?.querySelector('source')
          if (!image) {
            return ''
          }

          return getFirstUrlFromSrcset(
            image.getAttribute('srcset') || image.getAttribute('src')
          )
        })
        .filter((url) => !_.isEmpty(url))
        .slice(0, 10)
      return urls.map((image) => {
        const widthMatch = image.match(/resize_w(\d+)/)
        const width = widthMatch ? widthMatch[1] : null
        const imageUrl = width != null ? image.replace(`resize_w${width}`, 'resize_w1000') : image
        return appendProductImageHash(imageUrl)
      })
    }
  },
  [SCHEMA.PRODUCT_SPECS]: {
    type: 'custom',
    selector: '.page-product__breadcrumb ~ section > section:nth-child(2)',
    attribute: 'innerHTML',
    callback: (html = '') => {
      if (typeof html !== 'string' || _.isEmpty(html.trim())) {
        return []
      }

      const doc = new DOMParser().parseFromString(html, 'text/html')
      const productRows = Array.from(doc.querySelectorAll('h2~div'))
      const productSpecs = productRows
        .filter((row) => row && row.querySelector('.selection-box-unselected'))
        .map((row) => {
          const name = _.trim(_.get(row.parentNode?.querySelector('h2'), 'innerText', ''))
          const values = Array.from(row.querySelectorAll('.selection-box-unselected')).map((e) =>
            _.trim(e.innerText)
          )
          return { name, values }
        })
        .filter((spec) => !_.isEmpty(spec.name) && !_.isEmpty(spec.values))
        .slice(0, 2)
      return productSpecs.map((spec) => {
        const isUnmatch = _.some(spec.values, (value) =>
          !match({ label: spec.name, value }, true)
        )
        return {
          ...spec,
          isUnmatch
        }
      })
    }
  }
})

const useShopeeListing = () => {
  const { trigger: corsCheckTrigger } = useCorsCheck()
  const requestController = useCloneRequestController()
  const { fetchHtml } = useExternalHtml()
  const { getBrandIdByName } = useBrands()
  const { match } = useCategoryAttributes()
  const trigger = async (formValues) => {
    const [corsError] = await safeAwait(corsCheckTrigger())
    if (corsError) {
      throw corsError
    }

    const externalUrl = _.get(formValues, 'externalUrl')
    const text = await fetchHtml(externalUrl, {
      causeCode: CAUSE_CODE.EXTERNAL_CLONE_FETCH_SHOPEE_LISTING_ERROR
    })
    const schema = getShopeeSchema(requestController, { getBrandIdByName, match })
    const proposal = await getDataFromHtml(text, schema)
    if (_.isEmpty(_.get(proposal, SCHEMA.TITLE))) {
      const customError = getCustomError(CAUSE_CODE.EXTERNAL_CLONE_BLOCKED_ERROR)
      throwIfError({ httpError: customError })
    }

    return {
      proposal,
      error: null
    }
  }

  return { trigger }
}

export default useShopeeListing
